/**
 * Numeric bounds for everything an instructor types.
 *
 * These live here because the browser and the Worker must agree. When the
 * limits existed only in the Worker's Zod schemas, the form accepted a body
 * fat of 111, silently posted it, and surfaced a generic validation error with
 * no idea which field was wrong. When they exist only in the browser, the API
 * is unguarded. One table, imported by both.
 *
 * The bounds are deliberately loose. Their job is catching a typo — 1750 for
 * 175, 111 for 11 — not deciding what a plausible Pilates student looks like.
 */

export interface NumericLimit {
  min: number;
  max: number;
  /** Decimal places accepted; the field refuses a third one as it is typed. */
  decimals: number;
  /** Shown beside the field when the value falls outside. */
  label: string;
}

export const LIMITS = {
  /** Body weight, and the muscle figure when it is given in kilograms. */
  weightKg: { min: 20, max: 400, decimals: 1, label: '20 – 400 kg' },
  /** Every circumference: waist, hip, chest, arms, thighs. */
  circumferenceCm: { min: 10, max: 300, decimals: 1, label: '10 – 300 cm' },
  /** Body fat, and the muscle figure when it is given as a percentage. */
  percent: { min: 0, max: 100, decimals: 1, label: '0 – 100 %' },
  heightCm: { min: 80, max: 250, decimals: 1, label: '80 – 250 cm' },
  /**
   * A goal's own numbers. The unit is the instructor's free text, so there is
   * no unit-specific bound to apply — this only catches a runaway typo.
   */
  goalValue: { min: 0, max: 10000, decimals: 1, label: '0 – 10.000' },
  /** Lessons left in a package. Written onto one report, never stored. */
  remainingLessons: { min: 0, max: 999, decimals: 0, label: '0 – 999' },
} as const satisfies Record<string, NumericLimit>;

export type LimitKey = keyof typeof LIMITS;

/**
 * The muscle figure carries its unit with it (D-15), so its bound depends on
 * that unit: 60 % and 60 kg are both plausible, 111 % is not.
 */
export function muscleLimit(unit: 'percent' | 'kg'): NumericLimit {
  return unit === 'percent' ? LIMITS.percent : LIMITS.weightKg;
}

/**
 * Parses what an instructor typed. A Turkish keyboard produces a comma, and a
 * comma is what the rest of the product prints, so it has to be accepted here.
 *
 * Returns null for empty input — every measurement field is optional — and
 * undefined when the text is not a number at all, which the caller reports
 * rather than silently dropping.
 */
export function parseTypedNumber(raw: string): number | null | undefined {
  const trimmed = raw.trim().replace(',', '.');
  if (trimmed === '') return null;
  if (!/^-?\d*\.?\d*$/.test(trimmed)) return undefined;
  const value = Number(trimmed);
  return Number.isFinite(value) ? value : undefined;
}

/** True when the text could still grow into a valid number, so it may be typed. */
export function isPartialNumber(raw: string, limit: NumericLimit): boolean {
  if (raw === '') return true;
  const pattern =
    limit.decimals > 0 ? new RegExp(`^\\d*([.,]\\d{0,${limit.decimals}})?$`) : /^\d*$/;
  return pattern.test(raw);
}

/** Null when the value is inside its bounds, otherwise the message to show. */
export function checkLimit(value: number, limit: NumericLimit): string | null {
  if (value < limit.min || value > limit.max) return `Beklenen aralık: ${limit.label}`;
  return null;
}
